// src/components/TaskImages.jsx
//
// Up to two screenshots on a task: attach, look at, remove.
//
// Written as one self-contained control so any screen can carry it — the task
// form, a detail drawer, the assignment grid — without each one re-deciding how
// big an image may be or what happens when the second one is already there.
//
// NOTHING IN HERE WRITES TO THE SERVER. Picking a file prepares it in the
// browser; pressing the X on a saved image marks it; both land in state the
// PARENT owns and commits from its own save, through commitTaskImages.
//
// That is the whole design, and it exists because of a real bug:
//
//   The form has a Cancel button, and Cancel has to mean cancel. While this
//   control deleted as you clicked, removing an image and then backing out of
//   the dialog destroyed the image anyway — the one action on the form that
//   could not be undone was also the only one that ignored the button whose
//   entire job is undoing. Staging puts screenshots under the same rule as
//   every other field on the form: Update applies them, Cancel drops them.
//
// So the parent holds two things — the images added (`pending`) and the codes
// of the saved ones dropped (`removed`) — and hands them back here, so the grid
// shows what the task WILL look like rather than what it still is.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  MAX_TASK_IMAGES,
  ACCEPTED_TYPES,
  prepareImage,
  listTaskImages,
  fetchTaskImageUrl,
} from "../utils/taskImages";
import "../assets/css/taskImages.css";

const kb = (bytes) => {
  const n = Number(bytes) || 0;
  return n >= 1024 * 1024
    ? `${(n / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(n / 1024))} KB`;
};

/* One saved image. Its bytes are fetched separately and as a blob — the route
   is authenticated, and an <img> tag cannot carry a token. */
const SavedThumb = ({ image, onOpen, onRemove, readOnly, busy }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    let made = null;

    fetchTaskImageUrl(image.QueryImageCode)
      .then((u) => {
        if (!alive) {
          /* Arrived after unmount — revoke it here or it leaks for the life
             of the tab. */
          URL.revokeObjectURL(u);
          return;
        }
        made = u;
        setUrl(u);
      })
      .catch(() => alive && setFailed(true));

    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [image.QueryImageCode]);

  return (
    <figure className="tim__item">
      <button
        type="button"
        className="tim__shot"
        onClick={() => url && onOpen(url, image.FileName)}
        title={image.FileName || "Open"}
        disabled={!url}
      >
        {failed ? (
          <span className="tim__failed">
            <i className="bi bi-exclamation-triangle" />
            Could not load
          </span>
        ) : url ? (
          <img src={url} alt={image.FileName || "Task screenshot"} />
        ) : (
          /* The picture is a second request after the list, so the card is on
             screen before there is anything to put in it. A shimmer the shape
             of the image reads as "coming"; an empty grey box reads as
             "broken". */
          <span className="tim__shimmer" />
        )}
      </button>

      <figcaption className="tim__caption">
        <span className="tim__name" title={image.FileName}>
          {image.FileName || `Image ${image.SlotNo}`}
        </span>
        <span className="tim__size">{kb(image.FileSize)}</span>
      </figcaption>

      {!readOnly && (
        <button
          type="button"
          className="tim__remove"
          /* The blob URL goes up with it: the confirmation shows the picture,
             and it is already loaded here — fetching it again to ask about it
             would be a second round trip for bytes we are holding. */
          onClick={() => onRemove(image, url)}
          disabled={busy}
          title="Remove this image"
        >
          <i className="bi bi-x-lg" />
        </button>
      )}
    </figure>
  );
};

/* One image chosen on the form and not yet sent. */
const PendingThumb = ({ item, onOpen, onRemove, busy }) => (
  <figure className="tim__item is-pending">
    <button
      type="button"
      className="tim__shot"
      onClick={() => onOpen(item.dataUrl, item.fileName)}
      title={item.fileName}
    >
      <img src={item.dataUrl} alt={item.fileName} />
    </button>

    <figcaption className="tim__caption">
      <span className="tim__name" title={item.fileName}>
        {item.fileName}
      </span>
      <span className="tim__size">{kb(item.bytes)}</span>
    </figcaption>

    {/* Says the picture is not saved yet, because it looks exactly like one
        that is. Without it, abandoning the form feels like losing a file the
        screen had already accepted. */}
    <span className="tim__badge">Saves with the task</span>

    <button
      type="button"
      className="tim__remove"
      onClick={() => onRemove(item)}
      disabled={busy}
      title="Remove"
    >
      <i className="bi bi-x-lg" />
    </button>
  </figure>
);

/* A card with nothing in it yet — shown while the task's images are being
   listed, and again while a chosen file is being read and shrunk.
 *
 * Both are waits the person caused and can otherwise see no sign of: opening a
 * task that has screenshots, or picking a 4 MB phone screenshot that takes a
 * moment to resize before it can appear. Holding the card keeps the form from
 * jumping when the real one lands, and the caption says which wait it is. */
const PlaceholderThumb = ({ caption }) => (
  <figure
    className="tim__item is-loading"
    aria-hidden={caption ? undefined : "true"}
  >
    <div className="tim__shot tim__shot--static">
      <span className="tim__shimmer" />
    </div>
    <figcaption className="tim__caption">
      {caption ? (
        <span className="tim__name tim__name--muted">
          <i className="bi bi-arrow-clockwise sk-spin" />
          {caption}
        </span>
      ) : (
        <span className="tim__bar" />
      )}
    </figcaption>
  </figure>
);

const TaskImages = ({
  queryCode = null,
  pending = [],
  onPendingChange,
  /* QueryImageCodes of saved images the person has dropped. Applied by the
     parent's save; forgotten if the form is cancelled. */
  removed = [],
  onRemovedChange,
  readOnly = false,
  /* True while the parent is writing the task. Locks the controls so nothing
     can join a set of changes that is already on its way to the server. */
  saving = false,
  label = "Screenshots",
}) => {
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(Boolean(queryCode));
  /* How many files are being read and shrunk right now — a count, not a flag,
     so two files picked together hold two cards. */
  const [preparing, setPreparing] = useState(0);
  const [preview, setPreview] = useState(null);
  /* The image being removed, with the blob URL already loaded for it. Null the
     rest of the time. */
  const [confirming, setConfirming] = useState(null);
  const inputRef = useRef(null);
  /* Which list request is the current one. Reopening the dialog on a different
     task while the first list is still in flight would otherwise paint that
     task's images into this one. */
  const ticketRef = useRef(0);

  /* What the task will hold once the form is saved: the saved images minus the
     dropped ones, plus the added ones. Every count on screen comes from this,
     which is what lets somebody remove one image and add another instead of
     being told both slots are full. */
  const visible = saved.filter((s) => !removed.includes(s.QueryImageCode));
  const removedCount = saved.length - visible.length;
  const shown = visible.length + pending.length + preparing;
  const room = MAX_TASK_IMAGES - shown;
  const busy = preparing > 0 || saving;

  const reload = useCallback(() => {
    const ticket = ticketRef.current + 1;
    ticketRef.current = ticket;

    if (!queryCode) {
      setSaved([]);
      setLoading(false);
      return;
    }

    setSaved([]);
    setLoading(true);
    listTaskImages(queryCode)
      .then((rows) => {
        if (ticketRef.current === ticket) setSaved(rows);
      })
      .catch(() => {
        if (ticketRef.current === ticket) setSaved([]);
      })
      .finally(() => {
        if (ticketRef.current === ticket) setLoading(false);
      });
  }, [queryCode]);

  useEffect(() => {
    reload();
  }, [reload]);

  /* Escape closes the topmost layer only. Dismissing the confirmation and the
     full-size view with one key would mean pressing it once to cancel a delete
     and finding the preview gone too. */
  useEffect(() => {
    if (!preview && !confirming) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (confirming) setConfirming(null);
      else setPreview(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [preview, confirming]);

  const pick = async (event) => {
    const files = [...(event.target.files || [])];
    /* Cleared immediately: without this, choosing the same file twice in a row
       fires no change event the second time and the control looks dead. */
    event.target.value = "";
    if (!files.length) return;

    if (files.length > room) {
      toast.warning(
        room <= 0
          ? `A task can carry ${MAX_TASK_IMAGES} images, and both slots are full.`
          : `There is room for ${room} more image${room === 1 ? "" : "s"}.`,
      );
      if (room <= 0) return;
    }

    const take = files.slice(0, room);
    setPreparing(take.length);

    const accepted = [];
    for (const file of take) {
      try {
        // eslint-disable-next-line no-await-in-loop
        accepted.push(await prepareImage(file));
      } catch (err) {
        toast.error(`${file.name}: ${err.message}`);
      } finally {
        /* Card by card, so the first of two large files appears while the
           second is still being resized. */
        setPreparing((n) => Math.max(0, n - 1));
      }
    }

    if (accepted.length) onPendingChange?.([...pending, ...accepted]);
  };

  /* Marks it. Deletes nothing — the row stays until the parent saves. */
  const confirmRemove = () => {
    const image = confirming?.image;
    if (!image) return;
    setConfirming(null);
    onRemovedChange?.([...removed, image.QueryImageCode]);
  };

  const dropPending = (item) =>
    onPendingChange?.(pending.filter((p) => p !== item));

  const showEmpty = !loading && shown === 0;

  /* A read-only view of a task with no screenshots renders nothing at all.
   *
   * On a FORM, "No images yet" is an invitation and the empty state earns its
   * place. On a details dialog there is nothing to invite: a heading, a "0 of
   * 2" and a line saying there is nothing would appear on every task that has
   * no picture, which is most of them, pushing the history further down the
   * page to report an absence. Nothing to show, so nothing is shown.
   *
   * Also covers the moment before the list lands, so the dialog does not flash
   * an empty attachments block on its way to being right. */
  if (readOnly && (loading || shown === 0)) return null;

  return (
    <div className="tim">
      <div className="tim__head">
        <span className="tim__label">
          <i className="bi bi-paperclip" />
          {label}
          <em>{loading ? "loading…" : `${shown} of ${MAX_TASK_IMAGES}`}</em>
        </span>

        {!readOnly && (
          <button
            type="button"
            className="tim__add"
            onClick={() => inputRef.current?.click()}
            /* Dead until the list is in: how many slots are free is not known
               before it, and a button that takes a file it then has to refuse
               is worse than one that waits a moment. */
            disabled={busy || loading || room <= 0}
            title={
              room <= 0
                ? `A task can carry ${MAX_TASK_IMAGES} images`
                : "Attach an image"
            }
          >
            <i className={`bi ${busy ? "bi-arrow-clockwise sk-spin" : "bi-plus-lg"}`} />
            {room <= 0 ? "Both slots full" : "Add image"}
          </button>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES.join(",")}
          multiple
          hidden
          onChange={pick}
        />
      </div>

      {loading ? (
        <div className="tim__grid">
          <PlaceholderThumb />
          <PlaceholderThumb />
        </div>
      ) : showEmpty ? (
        <p className="tim__empty">
          {readOnly
            ? "No images attached."
            : "No images yet. A screenshot of the problem usually says more than the description does."}
        </p>
      ) : (
        <div className="tim__grid">
          {visible.map((image) => (
            <SavedThumb
              key={image.QueryImageCode}
              image={image}
              onOpen={(url, name) => setPreview({ url, name })}
              onRemove={(img, url) => setConfirming({ image: img, url })}
              readOnly={readOnly}
              busy={busy}
            />
          ))}

          {pending.map((item, i) => (
            <PendingThumb
              key={`${item.fileName}-${i}`}
              item={item}
              onOpen={(url, name) => setPreview({ url, name })}
              onRemove={dropPending}
              /* Locked while a file is being prepared too: pick() holds the
                 list it started with, and dropping one mid-prepare would be
                 undone the moment the new file joined it. */
              busy={busy}
            />
          ))}

          {Array.from({ length: preparing }, (_, i) => (
            <PlaceholderThumb key={`preparing-${i}`} caption="Preparing…" />
          ))}
        </div>
      )}

      {/* The card leaves the grid the moment it is dropped, which is the right
          feedback for "removed" and the wrong feedback for "removed, but not
          yet". This line carries the difference, and carries the way back:
          somebody who dropped the wrong one of two screenshots called 26.png
          and 27.png should not have to cancel the whole form to get it back. */}
      {removedCount > 0 && !readOnly && (
        <p className="tim__note">
          <i className="bi bi-clock-history" />
          <span>
            {removedCount === 1
              ? "1 image will be removed"
              : `${removedCount} images will be removed`}{" "}
            when you save the task.
          </span>
          <button
            type="button"
            className="tim__undo"
            onClick={() => onRemovedChange?.([])}
            disabled={saving}
          >
            Undo
          </button>
        </p>
      )}

      {/* Removing a saved image still asks, even though it is now undoable —
          it is the only change on this form that ends with bytes gone from the
          database, and the thumbnail is the only way to tell 26.png from
          27.png. In the app rather than through window.confirm: the browser's
          own box is chrome-coloured, lands at the top of the screen away from
          the thing it is about, cannot show the picture, and on a second
          dialog offers to "prevent this page from creating more dialogs" —
          which turns the next confirmation into a silent yes. */}
      {confirming && (
        <div
          className="tim__confirm"
          role="dialog"
          aria-modal="true"
          onClick={() => setConfirming(null)}
        >
          <div className="tim__confirm-box" onClick={(e) => e.stopPropagation()}>
            <div className="tim__confirm-head">
              <span className="tim__confirm-icon">
                <i className="bi bi-trash3" />
              </span>
              <div>
                <h6>Remove this image?</h6>
                <p>
                  It leaves the task when you save the form. Cancel instead and
                  it stays exactly where it is.
                </p>
              </div>
            </div>

            {confirming.url && (
              <div className="tim__confirm-shot">
                <img src={confirming.url} alt={confirming.image?.FileName || ""} />
              </div>
            )}

            <div className="tim__confirm-file">
              <i className="bi bi-image" />
              <span className="tim__name">
                {confirming.image?.FileName || `Image ${confirming.image?.SlotNo}`}
              </span>
              <span className="tim__size">{kb(confirming.image?.FileSize)}</span>
            </div>

            <div className="tim__confirm-actions">
              <button
                type="button"
                className="tim__btn"
                onClick={() => setConfirming(null)}
              >
                Keep it
              </button>
              {/* autoFocus on the SAFE choice. The destructive one should never
                  be a stray Enter away. */}
              <button
                type="button"
                className="tim__btn is-danger"
                onClick={confirmRemove}
                autoFocus
              >
                <i className="bi bi-trash3" />
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full size. A 260px thumbnail of a screenshot is unreadable, which is
          the whole reason somebody attached one. */}
      {preview && (
        <div className="tim__lightbox" onClick={() => setPreview(null)} role="dialog">
          <button type="button" className="tim__lightbox-close" aria-label="Close">
            <i className="bi bi-x-lg" />
          </button>
          <img src={preview.url} alt={preview.name} onClick={(e) => e.stopPropagation()} />
          <span className="tim__lightbox-name">{preview.name}</span>
        </div>
      )}
    </div>
  );
};

export default TaskImages;
